import { ActionType } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { useIsAllowed, useRegionId } from "store/selectors/hooks";
import { stableQueryOptionsRpc } from "util/requests";

export type SharedPipeline = inferProcedureOutput<
    AppRouter["sharedPipeline"]["list"]
>[number];
export type SharedPipelineStatus = SharedPipeline["statuses"][number];
export type OutgoingPipelineShare = inferProcedureOutput<
    AppRouter["sharedPipeline"]["outgoing"]
>[number];

const noSharedPipelines: SharedPipeline[] = [];
const noOutgoingShares: OutgoingPipelineShare[] = [];

export const useOutgoingPipelineShares = (): OutgoingPipelineShare[] => {
    const regionId = useRegionId();
    const allowed = useIsAllowed(ActionType.LoadHomeCareLeads);
    const trpcContext = trpc.useTRPC();
    const enabled = !!regionId && allowed;
    const query = useQuery({
        ...trpcContext.sharedPipeline.outgoing.queryOptions({ regionId }),
        ...stableQueryOptionsRpc(),
        staleTime: 60 * 1000,
        enabled,
    });
    return enabled && query.data ? query.data : noOutgoingShares;
};

export const useSharedPipelines = (): {
    pipelines: SharedPipeline[];
    isLoading: boolean;
    isError: boolean;
} => {
    const regionId = useRegionId();
    const allowed = useIsAllowed(ActionType.ViewSharedPipeline);
    const trpcContext = trpc.useTRPC();
    const enabled = !!regionId && allowed;
    const query = useQuery({
        ...trpcContext.sharedPipeline.list.queryOptions({ regionId }),
        ...stableQueryOptionsRpc(),
        staleTime: 60 * 1000,
        enabled,
    });
    return {
        pipelines: enabled && query.data ? query.data : noSharedPipelines,
        isLoading: enabled && query.isLoading,
        isError: enabled && query.isError,
    };
};
